import { UseFormReturn, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "../../core/translation.core";
import { CustomerList, CustomerListFilter } from "../../types/customer";
import CustomerListRow from "./customerListRow.comp";
import { ChevronSmallDownIcon, ChevronSmallUpIcon, ChevronUpDownIcon } from "../../icons/chevronUpDown.icon";
import customerCore from "../../core/customer.core";

export default function CustomerListComp({
  customers,
  methods,
}: {
  customers: CustomerList;
  methods: UseFormReturn<CustomerListFilter, any>;
}) {
  const { t, msg } = useTranslation();
  const customerIds = customers?.list.map((customer) => customer.id) || [];
  const { data: rewardPointsData, isLoading: rewardPointsLoading } = useQuery({
    queryKey: ["rewardPointsBatch", customerIds],
    queryFn: () => customerCore.getRewardPointsBatch(customerIds),
    enabled: customerIds.length > 0,
  });
  const { data: giftCardPointsData, isLoading: giftCardPointsLoading } = useQuery({
    queryKey: ["giftCardPointsBatch", customerIds],
    queryFn: () => customerCore.getGiftCardPointsBatch(customerIds),
    enabled: customerIds.length > 0,
  });

  return (
    <div id="product-section">
      <div className="hidden max-w-[1200px] lg:grid lg:grid-cols-[300px_100px_170px_60px_60px_100px_160px_140px] gap-6 py-2 border-b-[1px]">
        <Title title={t(msg.setting.email)} name="email" methods={methods} />
        <Title title={t(msg.customer.name)} name="firstName" methods={methods} />
        <Title title={t(msg.customer.mobile)} name="mobileNumber" methods={methods} />
        <Title title={t(msg.customer.provider)} name="provider" methods={methods} />
        <div className="text-right">{t(msg.reward.pointsShort)}</div>
        <div className="text-right">{t(msg.reward.shopDollar)}</div>
        <Title title={t(msg.import.createdAt)} name="createdAt" methods={methods} />
        <div className="text-right hidden lg:block">{t(msg.membership.membership)}</div>
      </div>

      {customers?.list.map((p) => (
        <CustomerListRow
          customer={p}
          key={p.id}
          rewardPoints={rewardPointsData?.pointsByCustomerId[p.id]}
          rewardPointsLoading={rewardPointsLoading}
          giftCardPoints={giftCardPointsData?.pointsByCustomerId[p.id]}
          giftCardPointsLoading={giftCardPointsLoading}
        />
      ))}
    </div>
  );
}

function Title({
  methods,
  title,
  name,
}: {
  methods: UseFormReturn<CustomerListFilter, any>;
  title: string;
  name: "email" | "firstName" | "lastName" | "mobileNumber" | "createdAt" | "provider";
}) {
  const [sort, isSortAsec] = useWatch({
    name: ["sort", "sortAsec"],
    control: methods.control,
  });

  function click() {
    methods.setValue("sort", name);
    methods.setValue("sortAsec", sort !== name ? 1 : isSortAsec === 1 ? -1 : 1);
  }

  return (
    <button id={`sort_${name}`} className={`flex items-center gap-2`} onClick={click}>
      {title}
      {sort !== name && <ChevronUpDownIcon />}
      {sort === name && isSortAsec === 1 && <ChevronSmallUpIcon />}
      {sort === name && isSortAsec === -1 && <ChevronSmallDownIcon />}
    </button>
  );
}
